import React, { useState } from 'react';
import {
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../../resources/strings/StringKeys';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { addTimeClockEntry } from '../../../../../repository/TimeClockDAO';
import { useToast, ToastType } from '../../../../../context/toast/ToastContext';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import BaseModal from '../../../../../components/modals/BaseModal';

function AddTimeClockModal({ show, onClose, organizationId, employees }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [clockInTime, setClockInTime] = useState(null);
    const [clockOutTime, setClockOutTime] = useState(null);
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);
    const [employeeError, setEmployeeError] = useState(false);
    const [clockInError, setClockInError] = useState(false);
    const [clockOutError, setClockOutError] = useState(false);
    const [reasonError, setReasonError] = useState(false);
    const { firebaseUser } = useUserContext();

    const resetForm = () => {
        setSelectedEmployee('');
        setClockInTime(null);
        setClockOutTime(null);
        setReason('');
        setEmployeeError(false);
        setClockInError(false);
        setClockOutError(false);
        setReasonError(false);
    };

    const handleSubmit = async () => {
        let isValid = true;

        if (!selectedEmployee) {
            setEmployeeError(true);
            showToast(t(StringKeys.PLEASE_SELECT_EMPLOYEE), ToastType.DANGER);
            isValid = false;
        }

        if (!clockInTime) {
            setClockInError(true);
            showToast(t(StringKeys.CLOCK_IN_TIME_REQUIRED), ToastType.DANGER);
            isValid = false;
        }

        if (!clockOutTime) {
            setClockOutError(true);
            showToast(t(StringKeys.CLOCK_OUT_TIME_REQUIRED), ToastType.DANGER);
            isValid = false;
        }

        if (clockInTime && clockOutTime && clockOutTime.isBefore(clockInTime)) {
            setClockOutError(true);
            showToast(t(StringKeys.CLOCK_OUT_BEFORE_CLOCK_IN), ToastType.DANGER);
            isValid = false;
        }

        if (!reason.trim()) {
            setReasonError(true);
            showToast(t(StringKeys.REASON_REQUIRED), ToastType.DANGER);
            isValid = false;
        }

        if (!isValid) return;

        setLoading(true);
        try {
            const result = await addTimeClockEntry(organizationId, {
                employee_id: selectedEmployee,
                timestamp_in: clockInTime.toDate(),
                timestamp_out: clockOutTime.toDate(),
                adjustments: [{
                    type: 'creation',
                    timestamp: new Date(),
                    reason: reason,
                    performed_by: firebaseUser.uid,
                    source: 'manual'
                }],
                source: 'manual',
                created_by: firebaseUser.uid
            });
            
            if (result.success) {
                showToast(t(StringKeys.TIME_CLOCK_ENTRY_ADDED), ToastType.SUCCESS);
                resetForm();
                onClose();
            } else {
                showToast(result.error || t(StringKeys.ERROR_ADDING_TIME_CLOCK), ToastType.DANGER);
            }
        } catch (error) {
            console.error('Error adding time clock entry:', error);
            showToast(t(StringKeys.ERROR_ADDING_TIME_CLOCK), ToastType.DANGER);
        }
        setLoading(false);
    };

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onClose();
            }}
            maxWidth='sm'
            title={t(StringKeys.ADD_TIME_CLOCK_ENTRY)}
            actions={
                <>
                    <Button onClick={() => {
                        resetForm();
                        onClose();
                    }}>{t(StringKeys.CANCEL)}</Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained" 
                        disabled={loading}
                    >
                        {t(StringKeys.SAVE)}
                    </Button>
                </>
            }
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormControl fullWidth error={employeeError}>
                        <InputLabel>{t(StringKeys.EMPLOYEE)}</InputLabel>
                        <Select
                            value={selectedEmployee}
                            onChange={(e) => {
                                setSelectedEmployee(e.target.value);
                                setEmployeeError(false);
                            }}
                            label={t(StringKeys.EMPLOYEE)}
                        >
                            {employees.map(emp => (
                                <MenuItem key={emp.id} value={emp.id}>
                                    {emp.first_name} {emp.last_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label={t(StringKeys.CLOCK_IN_TIME)}
                            value={clockInTime}
                            onChange={(newValue) => {
                                setClockInTime(newValue);
                                setClockInError(false);
                            }}
                            slotProps={{ 
                                textField: { 
                                    fullWidth: true,
                                    error: clockInError
                                } 
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            label={t(StringKeys.CLOCK_OUT_TIME)}
                            value={clockOutTime}
                            onChange={(newValue) => {
                                setClockOutTime(newValue);
                                setClockOutError(false);
                            }}
                            slotProps={{ 
                                textField: { 
                                    fullWidth: true,
                                    error: clockOutError
                                } 
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t(StringKeys.REASON)}
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            setReasonError(false);
                        }}
                        multiline
                        rows={2}
                        error={reasonError}
                    />
                </Grid>
            </Grid>
        </BaseModal>
    );
}

export default AddTimeClockModal; 